/**
 * DataFast analytics helpers (client-side).
 *
 * Tracker: index.html / index.template.html → script.js (cookies).
 * Public website id: data-website-id=dfid_… (safe in HTML).
 * Brand-scoped: always attach BRAND_SOURCE_KEY as source / brand_source_key on Stripe.
 * data-disable-payments=true — revenue comes only from Stripe metadata
 * (server-side), not from success-URL session_id auto-detection.
 *
 * Reads datafast_visitor_id + datafast_session_id cookies and forwards them to
 * Stripe Checkout Session / PaymentIntent metadata (api/lib/datafastAttribution.ts).
 *
 * Docs: https://datafa.st/docs/stripe-checkout-api
 * Duplicate payments: https://datafa.st/docs/fix-duplicate-payments
 */

import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";

export const DATAFAST_VISITOR_COOKIE = "datafast_visitor_id";
export const DATAFAST_SESSION_COOKIE = "datafast_session_id";

export type DataFastGoalParams = Record<string, string | number | boolean | undefined | null>;

declare global {
  interface Window {
    datafast?: {
      (...args: unknown[]): void;
      q?: unknown[];
    };
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  try {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`));
    if (!match) return undefined;
    const value = decodeURIComponent(match.slice(name.length + 1).trim());
    return value || undefined;
  } catch {
    return undefined;
  }
}

/** DataFast visitor + session IDs from first-party cookies (required for revenue attribution). */
export function getDataFastIds(): {
  datafast_visitor_id?: string;
  datafast_session_id?: string;
} {
  const visitor = readCookie(DATAFAST_VISITOR_COOKIE)?.slice(0, 200);
  const session = readCookie(DATAFAST_SESSION_COOKIE)?.slice(0, 200);
  const out: { datafast_visitor_id?: string; datafast_session_id?: string } = {};
  if (visitor) out.datafast_visitor_id = visitor;
  if (session) out.datafast_session_id = session;
  return out;
}

/**
 * Stripe metadata keys for DataFast revenue attribution + brand binding.
 * Spread into Checkout Session / PaymentIntent metadata (and subscription_data.metadata).
 *
 * Always sets source + brand_source_key = BRAND_SOURCE_KEY so revenue on a shared
 * Stripe account is filterable by brand. Visitor/session IDs when cookies exist.
 *
 * UTM / ref / via are NOT required here: DataFast attributes channels via
 * visitor_id + session_id cookies (https://datafa.st/docs/utm-tracking).
 */
export function getDataFastStripeMetadata(): Record<string, string> {
  const ids = getDataFastIds();
  const out: Record<string, string> = {
    source: BRAND_SOURCE_KEY,
    brand_source_key: BRAND_SOURCE_KEY,
  };
  if (ids.datafast_visitor_id) out.datafast_visitor_id = ids.datafast_visitor_id;
  if (ids.datafast_session_id) out.datafast_session_id = ids.datafast_session_id;
  return out;
}

export {
  buildCampaignUrl,
  DATAFAST_TRACKING_PARAM_KEYS,
  getFirstVisitTrackingParams,
  parseDataFastTrackingParams,
} from "@/lib/firstVisitAttribution";

function sanitizeGoalParams(
  params?: DataFastGoalParams,
): Record<string, string> | undefined {
  if (!params || typeof params !== "object") return undefined;
  const out: Record<string, string> = {};
  let count = 0;
  for (const [rawKey, rawVal] of Object.entries(params)) {
    if (count >= 10) break;
    if (rawVal == null) continue;
    const key = String(rawKey)
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "_")
      .slice(0, 64);
    if (!key) continue;
    const value = String(rawVal).slice(0, 255);
    if (!value) continue;
    out[key] = value;
    count += 1;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Track a custom goal (funnel step). Safe no-op if script not loaded.
 * Goal names: lowercase, numbers, _, -, : ; max 64 chars.
 * Do NOT use reserved payment-provider names (payment, subscription_started, …).
 */
export function trackDataFastGoal(goalName: string, params?: DataFastGoalParams): void {
  if (typeof window === "undefined") return;
  const name = String(goalName || "")
    .toLowerCase()
    .replace(/[^a-z0-9_:-]/g, "_")
    .slice(0, 64);
  if (!name) return;

  const cleaned = sanitizeGoalParams(params);
  try {
    if (typeof window.datafast === "function") {
      if (cleaned) window.datafast(name, cleaned);
      else window.datafast(name);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Identify authenticated user (Clerk). Call after sign-in / on session restore.
 * user_id is required (prefer stable Clerk user.id over email).
 */
export function identifyDataFastUser(
  userId: string,
  traits?: {
    name?: string | null;
    image?: string | null;
    email?: string | null;
    [key: string]: string | number | boolean | null | undefined;
  },
): void {
  if (typeof window === "undefined") return;
  const id = String(userId || "").trim().slice(0, 255);
  if (!id) return;

  const payload: Record<string, string> = { user_id: id };
  if (traits && typeof traits === "object") {
    if (traits.name) payload.name = String(traits.name).slice(0, 255);
    if (traits.image) payload.image = String(traits.image).slice(0, 250);
    let custom = 0;
    for (const [k, v] of Object.entries(traits)) {
      if (k === "name" || k === "image" || k === "user_id") continue;
      if (v == null) continue;
      if (custom >= 10) break;
      const key = String(k)
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, "_")
        .slice(0, 64);
      if (!key || key === "user_id" || key === "name" || key === "image") continue;
      payload[key] = String(v).slice(0, 255);
      custom += 1;
    }
  }

  try {
    if (typeof window.datafast === "function") {
      window.datafast("identify", payload);
    }
  } catch {
    /* ignore */
  }
}
